/**
 * Landing attribution capture (SRC-04, DEC-18: last touch reported, first touch stored).
 *
 * On a public job page we read utm_source / utm_medium / utm_campaign and the
 * recruiter ref (`r` query param, or the /jobs/<slug>/r/<ref> path alias) plus
 * document.referrer, and keep:
 *   - last touch  -> sessionStorage (this visit), overwritten by every tagged landing
 *   - first touch -> localStorage, kept 30 days, never overwritten while fresh
 * keyed per job and also as a "latest" entry for pages that only know the job id.
 *
 * Only whitelisted, non-PII values are stored: channel/medium enums, a slug-like
 * campaign, an 8-char opaque ref, and the referrer reduced to origin + path (the
 * query string and fragment can carry emails or tokens, so they are dropped).
 * Nothing here ever writes to the URL.
 */
import { baseURL } from "../api/instance";

export const CHANNEL_MEDIUM = {
  direct: "direct",
  email: "email",
  linkedin: "social",
  whatsapp: "social",
  indeed: "job_board",
  naukri: "job_board",
};
// "referral" (candidate share) and "share" (generic public link) are kept here so the
// tag travels; the backend maps mediums it does not know to the channel's default.
const MEDIUMS = ["social", "job_board", "email", "direct", "referral", "share"];
const REF_RE = /^[a-z2-7]{8}$/;
const FIRST_TOUCH_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const LAST_PREFIX = "haire_attr_last:";
const FIRST_PREFIX = "haire_attr_first:";
const VISIT_PREFIX = "haire_attr_visit:";
const LATEST = "*";

// document.referrer does not change on SPA navigation, so it is only meaningful
// for the first capture of a page load (e.g. /jobs/:id redirecting to related-jobs
// must not re-record the same referrer as a new, untagged touch).
let referrerConsumed = false;
export const __resetReferrerForTests = () => {
  referrerConsumed = false;
};

const safeStorage = (kind) => {
  try {
    const store = typeof window !== "undefined" ? window[kind] : null;
    if (!store) return null;
    const probe = "__haire_probe__";
    store.setItem(probe, "1");
    store.removeItem(probe);
    return store;
  } catch (e) {
    return null; // private mode / blocked storage: attribution is best effort
  }
};

const readJson = (store, key) => {
  if (!store) return null;
  try {
    const raw = store.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeJson = (store, key, value) => {
  if (!store) return;
  try {
    store.setItem(key, JSON.stringify(value));
  } catch (e) {
    /* quota / blocked storage */
  }
};

const normKey = (value) => {
  const text = value === undefined || value === null ? "" : String(value).trim();
  return text && text !== "undefined" && text !== "null" ? text : "";
};

export const cleanChannel = (value) => {
  const v = String(value || "").trim().toLowerCase();
  return CHANNEL_MEDIUM[v] ? v : null;
};

export const cleanRef = (value) => {
  const v = String(value || "").trim().toLowerCase();
  return REF_RE.test(v) ? v : null;
};

const cleanCampaign = (value) => {
  const v = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "")
    .slice(0, 255);
  return v || null;
};

/** origin + path only; "" for same-site or unparsable referrers. */
export const cleanReferrer = (value, ownOrigin) => {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    if (ownOrigin && url.origin === ownOrigin) return null; // internal navigation
    return `${url.origin}${url.pathname}`.slice(0, 500);
  } catch (e) {
    return null;
  }
};

/** Ref from the /jobs/<slug>/r/<ref> path alias, if present. */
export const refFromPath = (pathname) => {
  const match = /\/jobs\/[^/]+\/r\/([^/?#]+)/.exec(pathname || "");
  return match ? cleanRef(decodeURIComponent(match[1])) : null;
};

/**
 * A touch from the landing URL, or null when the visit carries nothing
 * (no utm, no ref, no external referrer).
 */
export function readLandingTouch({ search = "", pathname = "", referrer = "", origin = "", now = Date.now() } = {}) {
  const params = new URLSearchParams(search || "");
  const channel = cleanChannel(params.get("utm_source"));
  const ref = cleanRef(params.get("r")) || refFromPath(pathname);
  const cleanedReferrer = cleanReferrer(referrer, origin);
  if (!channel && !ref && !cleanedReferrer) return null;
  const medium = String(params.get("utm_medium") || "").toLowerCase();
  return {
    channel,
    medium: MEDIUMS.includes(medium) ? medium : channel ? CHANNEL_MEDIUM[channel] : null,
    campaign: cleanCampaign(params.get("utm_campaign")),
    ref,
    referrer: cleanedReferrer,
    at: new Date(now).toISOString(),
  };
}

const fresh = (touch, now) => {
  if (!touch || !touch.at) return false;
  const at = Date.parse(touch.at);
  return Number.isFinite(at) && now - at <= FIRST_TOUCH_TTL_MS && at <= now + 60000;
};

/**
 * Store a landing touch for a job. `jobKeys` are the identifiers the page knows
 * (slug and/or id). Returns the stored touch (or null).
 */
export function captureLandingAttribution(jobKeys = [], location, { referrer, now = Date.now() } = {}) {
  const loc = location || (typeof window !== "undefined" ? window.location : {});
  const pageReferrer =
    referrer !== undefined ? referrer : typeof document !== "undefined" ? document.referrer : "";
  const touch = readLandingTouch({
    search: loc.search,
    pathname: loc.pathname,
    referrer: referrerConsumed ? "" : pageReferrer,
    origin: loc.origin || (typeof window !== "undefined" ? window.location.origin : ""),
    now,
  });
  referrerConsumed = true;
  if (!touch) return null;
  const session = safeStorage("sessionStorage");
  const local = safeStorage("localStorage");
  const keys = [...new Set([...jobKeys.map(normKey).filter(Boolean), LATEST])];
  keys.forEach((key) => {
    writeJson(session, LAST_PREFIX + key, touch);
    if (!fresh(readJson(local, FIRST_PREFIX + key), now)) writeJson(local, FIRST_PREFIX + key, touch);
  });
  return touch;
}

/** Make a job's touches reachable by another identifier (e.g. slug page -> id at apply time). */
export function aliasAttribution(fromKey, toKey, { now = Date.now() } = {}) {
  const from = normKey(fromKey);
  const to = normKey(toKey);
  if (!from || !to || from === to) return;
  const session = safeStorage("sessionStorage");
  const local = safeStorage("localStorage");
  const last = readJson(session, LAST_PREFIX + from);
  if (last && !readJson(session, LAST_PREFIX + to)) writeJson(session, LAST_PREFIX + to, last);
  const first = readJson(local, FIRST_PREFIX + from);
  if (fresh(first, now) && !fresh(readJson(local, FIRST_PREFIX + to), now)) writeJson(local, FIRST_PREFIX + to, first);
}

/**
 * The `attribution` body field for apply / lead-create:
 * { channel, medium, campaign, ref, referrer, at, first_touch } or undefined.
 */
export function getAttributionPayload(jobKeys = [], opts = {}) {
  // On the apply / lead-submit path: tracking must never break a submission.
  try {
    return attributionPayload(jobKeys, opts);
  } catch (e) {
    return undefined;
  }
}

function attributionPayload(jobKeys = [], { now = Date.now() } = {}) {
  const session = safeStorage("sessionStorage");
  const local = safeStorage("localStorage");
  const keys = [...jobKeys.map(normKey).filter(Boolean), LATEST];
  const pick = (store, prefix, check) => {
    for (const key of keys) {
      const touch = readJson(store, prefix + key);
      if (touch && (!check || check(touch))) return touch;
    }
    return null;
  };
  const first = pick(local, FIRST_PREFIX, (t) => fresh(t, now));
  const last = pick(session, LAST_PREFIX) || first;
  if (!last) return undefined;
  return { ...last, first_touch: first || last };
}

/**
 * SRC-10 click beacon: once per session per job, only for tagged visits. No PII
 * is sent (job slug, channel enum, opaque ref). Never throws.
 */
export function sendVisitBeacon(jobKey, touch, { fetchImpl } = {}) {
  const key = normKey(jobKey);
  if (!key || !touch || (!touch.channel && !touch.ref)) return false;
  const session = safeStorage("sessionStorage");
  if (session) {
    try {
      if (session.getItem(VISIT_PREFIX + key)) return false;
      session.setItem(VISIT_PREFIX + key, "1");
    } catch (e) {
      /* ignore */
    }
  }
  const doFetch = fetchImpl || (typeof fetch !== "undefined" ? fetch : null);
  if (!doFetch) return false;
  try {
    doFetch(`${baseURL}job/${encodeURIComponent(key)}/visit/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ channel: touch.channel || "", ref: touch.ref || "" }),
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
  } catch (e) {
    return false;
  }
  return true;
}

/** Capture + beacon in one call for public job landing components. */
export function trackJobLanding(jobKeys = [], location) {
  // On the public job-link path: tracking must never break the redirect.
  try {
    const touch = captureLandingAttribution(jobKeys, location);
    const primary = jobKeys.map(normKey).find(Boolean);
    if (touch && primary) sendVisitBeacon(primary, touch);
    return touch;
  } catch (e) {
    return null;
  }
}
